"use client";

import { useEffect } from "react";
import { syncAdPixels } from "@/lib/ad-pixels";
import { setStoredConsent } from "@/lib/consent";
import { resolveAgeMode } from "@/lib/essential-only";
import { applyInternalTrafficControl, initGoogleAnalytics, isLiveHost } from "@/lib/analytics-init";

// WO-PHASE1-SEO-CUTOVER-002 · T2 — GA4, restored. The new build carried ZERO analytics: none in the
// HTML and none in any of the 11 JS chunks.
//
// HOW LIVE ACTUALLY DOES IT, read out of live's own bundle
// (_next/static/chunks/pages/_app-4b1b20d95596c131.js) rather than assumed. There is no gtag
// <script src> in live's HTML at all — the tag is injected client-side, so anyone grepping the
// document for "gtag" concludes live has no analytics. Live's code, deminified:
//
//   const id = domain === "snowbus" ? GTM_ID_SNOWBUS : GTM_ID_RESERVE;
//   if (id) {
//     hasCookie("gtagConsentCookie")
//       ? gtag("consent", "update",  { ad_storage:"granted", ad_user_data:"granted",
//                                      ad_personalization:"granted", analytics_storage:"granted" })
//       : gtag("consent", "default", { ...the same four, all granted });
//     initialize(id, {});   // react-ga4: appends googletagmanager.com/gtag/js?id=<id>, then js+config
//   }
//
// ⚠️ EXACTLY ONE PROPERTY, DELIBERATELY. Live ships two ids in __NEXT_DATA__ —
// GTM_ID_SNOWBUS "G-YMTRKT89WF" and GTM_ID_RESERVE "G-KTGNWSS795" — but they are the two arms of a
// ternary, never both. Only G-YMTRKT89WF has ever fired on snowbus.com. Wiring the reserve id as a
// second config would double-count every session and split the reports, which is a worse outcome than
// the zero analytics we are fixing. It is carried below as the branch it is, not as a second tag (see
// @/lib/analytics-init, which hardcodes the same single id for the same reason).
//
// Ordinary product analytics need no opt-in: the tag loads on production with analytics_storage granted.
// Google's ad_storage / ad_user_data / ad_personalization stay denied (see @/lib/consent). The Meta and
// LinkedIn advertising tags are @/lib/ad-pixels: loaded only after an explicit advertising grant, on the
// production hosts only (dry-run elsewhere), and stopped on revocation.
export default function Analytics() {
  useEffect(() => {
    // `?internal=1|0` (founder 13:48): applied and stripped on every host, before any tag is initialised.
    applyInternalTrafficControl();
    // A returning visitor's stored grant; a choice changed in another tab is picked up when this one is shown. The signed-in member's age
    // band is resolved FIRST (same-origin /api/auth/age sets or clears the youth flag from the backend), so no advertising tag loads for a
    // 13-15 member and an earlier grant on this browser is cleared. No session = nothing known = the adult contract, unchanged.
    // GA4 waits for the same answer: a youth member gets essential-only (no GA4, no identifiers), everyone else the existing contract.
    void resolveAgeMode().then((mode) => {
      // Youth: an earlier grant on this browser is cleared. Unresolved: essential-only for this page, the stored choice left as it is.
      if (mode === "youth") setStoredConsent("denied");
      syncAdPixels();
      if (isLiveHost(window.location.hostname)) initGoogleAnalytics();
    });
    const onVisible = () => {
      if (document.visibilityState === "visible") syncAdPixels();
    };
    document.addEventListener("visibilitychange", onVisible);
    // Product analytics run for every measurable production visit once the age band is resolved above; Google's advertising, remarketing
    // and personalization signals are denied inside initGoogleAnalytics(), whatever the stored choice.
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  return null;
}
